import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { nsSharedStyles } from '../ns-styles/index.js';

import { ShareableRelationship, connectionLabel, searchConnections } from '../helpers/shareable-connections.js';

import type { Option } from './ns-multi-select.js';
import './ns-multi-select.js';
import '@awesome.me/webawesome/dist/components/button/button.js';

@customElement('ns-connection-picker')
export class NSConnectionPicker extends LitElement {
  @property({ type: String })
  label = '';

  @property({ type: String, attribute: 'select-all-label' })
  selectAllLabel = 'Select All';

  @property({ type: String, attribute: 'share-with' })
  shareWith = '';

  @property({ attribute: false })
  connections: ShareableRelationship[] = [];

  @property({ attribute: false })
  selected: ReadonlySet<string> = new Set();

  @state()
  private __searchTerm = '';

  static styles = [
    nsSharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
      }

      .header {
        display: flex;
        margin-bottom: var(--wa-space-xs);
      }

      .header .title {
        flex: 1;
        font-size: var(--wa-font-size-m);
      }
    `,
  ];

  private __change(selected: Set<string>) {
    this.selected = selected;
    this.dispatchEvent(new CustomEvent('connections-change', { detail: { selected }, bubbles: true, composed: true }));
  }

  private __selectAll() {
    const confirmed = window.confirm(
      `This will share all ${this.connections.length} of your current connections with ${this.shareWith}. Continue?`,
    );
    if (confirmed) this.__change(new Set(this.connections.map((c) => c.relationshipId)));
  }

  private __onSearchInput(evt: CustomEvent<{ query: string }>) {
    evt.stopPropagation();
    this.__searchTerm = evt.detail.query;
  }

  private __onSelectionChanged(evt: CustomEvent<{ selected: Option[] }>) {
    evt.stopPropagation();
    this.__change(new Set(evt.detail.selected.map((option) => option.value)));
  }

  render() {
    const toOption = (relationship: ShareableRelationship): Option => ({
      value: relationship.relationshipId,
      label: connectionLabel(relationship),
    });
    const selected = this.connections.filter((r) => this.selected.has(r.relationshipId));

    return html`
      <div class="header">
        <div class="title">${this.label}</div>
        <wa-button variant="brand" type="button" @click=${this.__selectAll}>${this.selectAllLabel}</wa-button>
      </div>
      <ns-multi-select
        label=${this.label}
        placeholder="Company Name"
        .options=${searchConnections(this.connections, this.selected, this.__searchTerm).map(toOption)}
        .selected=${selected.map(toOption)}
        @search-input=${this.__onSearchInput}
        @selection-changed=${this.__onSelectionChanged}
      ></ns-multi-select>
      <slot></slot>
    `;
  }
}
